"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";

export default function EventTicketingBrandingCompare() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.12) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Your branding. Not theirs.", "Tu marca. No la suya.")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("They sell. ", "Ellos venden. ")}
            <span className="text-white/30">{t("We hand you the mic.", "Nosotros te damos el micrófono.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "On a big ticketing marketplace, every event page looks like every other event page. Your venue isn't every other venue. Your checkout shouldn't be either.",
              "En un gran marketplace de tickets, toda página de evento se ve igual a las demás. Tu venue no se parece a los demás. Tu checkout tampoco debería."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[960px] mx-auto items-start">
          {/* Generic other-platform mockup — gray */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-white/35 mb-4">
              {t("Other ticketing platform", "Otra plataforma de tickets")}
            </div>
            <BrowserFrame chrome="#3F3F3F">
              <div className="bg-white text-[#1A1A1A] flex flex-col h-full">
                {/* Generic platform header (gray, platform branding) */}
                <div className="bg-[#F8F7FA] border-b border-black/5 px-3 py-2 flex items-center gap-2">
                  <div className="text-[9px] font-black" style={{ color: "#6B7280" }}>
                    {t("ticket platform", "plataforma")}
                  </div>
                  <div className="flex-1" />
                  <div className="text-[7px] text-black/40">Sign in</div>
                </div>

                {/* Generic event banner — gray gradient */}
                <div className="bg-gradient-to-br from-[#D1D5DB] to-[#9CA3AF] h-[80px] flex items-end p-3">
                  <div>
                    <div className="text-[7px] font-bold uppercase tracking-wider text-white/85">
                      Friday Dec 13
                    </div>
                    <div className="text-[12px] font-black text-white">NEON RUSH</div>
                  </div>
                </div>

                {/* Tier list — plain */}
                <div className="p-3 space-y-2 flex-1">
                  <div className="border border-black/10 rounded p-2 flex justify-between items-center">
                    <div>
                      <div className="text-[8px] font-bold text-black">General Admission</div>
                      <div className="text-[7px] text-black/50">$40.00</div>
                    </div>
                    <div className="text-[7px] text-black/40 font-medium">
                      {t("+ service fee", "+ service fee")}
                    </div>
                  </div>
                  <div className="border border-black/10 rounded p-2 flex justify-between items-center opacity-60">
                    <div>
                      <div className="text-[8px] font-bold text-black">VIP</div>
                      <div className="text-[7px] text-black/50">$1,200.00</div>
                    </div>
                    <div className="text-[7px] text-black/40 font-medium">
                      {t("+ service fee", "+ service fee")}
                    </div>
                  </div>
                </div>

                {/* Fee notice */}
                <div className="bg-[#FFF7E6] border-t border-[#FBBF24]/40 px-3 py-2 text-[7px] text-black/70 leading-relaxed">
                  <span className="font-bold">Service fees</span> apply at checkout.
                </div>

                <div className="bg-[#6B7280] py-2 text-center">
                  <span className="text-[9px] font-bold text-white">Checkout</span>
                </div>
              </div>
            </BrowserFrame>

            <div className="mt-4 text-[12px] font-medium text-white/45 leading-relaxed">
              {t(
                "Their colors. Their fees. Their fonts. Their logo. Your event, sold from their stage.",
                "Sus colores. Sus fees. Sus fuentes. Su logo. Tu evento, vendido desde su escenario."
              )}
            </div>
          </motion.div>

          {/* Redy mockup — branded */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="text-[10px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
              {t("Your venue checkout", "Tu checkout")}
            </div>
            <BrowserFrame chrome="#1A1A1A" url="neonclub.com/neonrush">
              <div className="bg-[#0A0814] text-white flex flex-col h-full">
                {/* Custom hero with venue branding */}
                <div
                  className="h-[120px] relative overflow-hidden flex items-end p-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #7C3AED 0%, #F472B6 50%, #FB923C 100%)",
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-40"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.4) 0%, transparent 50%)",
                    }}
                  />
                  <div className="relative">
                    <div className="text-[7px] font-bold uppercase tracking-widest text-white/80">
                      Friday Dec 13 · 10 PM · 21+
                    </div>
                    <div className="text-[16px] font-black text-white leading-tight tracking-tight">
                      NEON RUSH
                    </div>
                    <div className="text-[7px] font-bold uppercase tracking-wider mt-0.5" style={{ color: "rgba(255,255,255,0.85)" }}>
                      NEON CLUB · Dallas
                    </div>
                  </div>
                </div>

                {/* Tier list — branded */}
                <div className="p-3 space-y-1.5 flex-1">
                  <div
                    className="rounded-[8px] p-2 border"
                    style={{
                      backgroundColor: "rgba(244,114,182,0.10)",
                      borderColor: "rgba(244,114,182,0.35)",
                    }}
                  >
                    <div className="flex justify-between items-baseline">
                      <div className="text-[8px] font-black text-white">General Admission</div>
                      <div className="text-[9px] font-black text-white tabular-nums">$40</div>
                    </div>
                    <div className="text-[7px] font-bold uppercase tracking-wider mt-0.5" style={{ color: "#F472B6" }}>
                      234 sold
                    </div>
                  </div>
                  <div className="bg-white/[0.04] border border-white/10 rounded-[8px] p-2">
                    <div className="flex justify-between items-baseline">
                      <div className="text-[8px] font-black text-white">VIP Booth</div>
                      <div className="text-[9px] font-black text-white tabular-nums">$1,200</div>
                    </div>
                    <div className="text-[7px] font-bold uppercase tracking-wider mt-0.5 text-white/50">
                      Only 6 left
                    </div>
                  </div>
                </div>

                {/* Apple Pay primary */}
                <div className="px-3 pb-3">
                  <div className="bg-black rounded-full py-2 text-center">
                    <span className="text-[9px] font-bold text-white"> Pay</span>
                  </div>
                  <div className="text-center text-[7px] text-white/40 mt-1.5">
                    {t("No platform fee per ticket.", "Sin fee de plataforma por ticket.")}
                  </div>
                </div>
              </div>
            </BrowserFrame>

            <div className="mt-4 text-[12px] font-medium text-white/65 leading-relaxed">
              {t(
                "Your colors. Your photos. Your logo. Apple Pay primary. No platform fee added at the last step.",
                "Tus colores. Tus fotos. Tu logo. Apple Pay primero. Sin fee de plataforma agregado al final."
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function BrowserFrame({
  children,
  chrome = "#1A1A1A",
  url = "ticketing-platform.com/e/neon-rush",
}: {
  children: React.ReactNode;
  chrome?: string;
  url?: string;
}) {
  return (
    <div className="rounded-[14px] overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.5)] border border-white/5">
      <div className="px-3 py-2 flex items-center gap-2" style={{ backgroundColor: chrome }}>
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#FF5F57]" />
          <div className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
          <div className="w-2 h-2 rounded-full bg-[#28C840]" />
        </div>
        <div className="flex-1 bg-black/30 rounded px-2 py-0.5 text-[8px] text-white/50 truncate">
          {url}
        </div>
      </div>
      <div className="h-[320px]">{children}</div>
    </div>
  );
}
